import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  checkIfPrintTextOnProduct,
  checkTextNotAllowedForView,
} from '@/utils/view/checkTextNotAllowed';
import { getPrintTypesForPrintArea } from '@/model/printType/getPrintTypesForPrintArea';
import { getDefaultPrintAreaForView } from '@/model/productType/getDefaultPrintAreaForView';
import { getIsStickerAppearance } from '@/model/productType/getIsStickerAppearance';
import { isMacroView } from '@/model/productType/isMacroView';
import { getFonts } from '@/redux/core/getters/getFonts';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getCurrentView } from '@/redux/draft/getters/getCurrentView';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { setWarningAction } from '@/components/draft/draftState/actions/setWarningAction';

export const useCanPrintOnView = () => {
  const productType = useSelector(getProductType);
  const appearance = useSelector(getCurrentAppearance);
  const view = useSelector(getCurrentView);
  const configurations = useSelector(selectConfigurations);
  const fonts = useSelector(getFonts);

  const defaultPrintArea = getDefaultPrintAreaForView(productType, view);
  const canPrintOnView = Boolean(
    defaultPrintArea && getPrintTypesForPrintArea(defaultPrintArea, appearance).length
  );

  const { isTextNotAllowedForView } = useMemo(() => {
    return checkTextNotAllowedForView(configurations, productType.printAreas, view.id);
  }, [configurations, productType.printAreas, view.id]);

  const canPrintTextOnProduct = useMemo(
    () => checkIfPrintTextOnProduct(productType.appearances, fonts[0].printTypeIds),
    [productType.appearances, fonts]
  );

  const canPrintTextOnView = useMemo(() => {
    if (!canPrintOnView || !canPrintTextOnProduct || isTextNotAllowedForView) return false;
    return appearance.printTypes.some((printType) => fonts[0].printTypeIds.includes(printType.id));
  }, [canPrintOnView, appearance, fonts, isTextNotAllowedForView, canPrintTextOnProduct]);

  const isSticker = getIsStickerAppearance(appearance);

  const draftDispatch = useReduxDraftDispatch();
  const showDisabledHint = isMacroView(view)
    ? () => draftDispatch(setWarningAction('macroView', 'macroview.hint'))
    : !canPrintTextOnView
    ? //TODO More general warning for cannot print text
      isSticker
      ? () => draftDispatch(setWarningAction('stickerNoText', 'sticker.noText.hint'))
      : // TODO Warning for text disabled on view
        undefined
    : //TODO More genral warning for product types with no valid print area
      undefined;

  return [canPrintOnView, canPrintTextOnView, showDisabledHint, canPrintTextOnProduct] as const;
};
